import React from "react";
import clsx from "clsx";
import { SelectInputOption } from "apps/user/learner/api/types";
import { splitQuestionTitle } from "./questionTitle";
import AssessmentUpliftHeader from "./AssessmentUpliftHeader";
import ProgressPill, { ProgressPillProps } from "./ProgressPill";
import AssessmentStepTransition from "./AssessmentStepTransition";
import styles from "./AssessmentQuestionUplift.module.scss";

export interface AssessmentQuestionUpliftProps {
  stepKey: string;
  titleHtml?: string | null;
  subtitleHtml?: string | null;
  options: SelectInputOption[];
  selectedOptionId?: string | null;
  onSelectOption: (option: SelectInputOption) => void;
  onBack?: () => void;
  onClose: () => void;
  progressPill?: ProgressPillProps | null;
}

const AssessmentQuestionUplift: React.FC<AssessmentQuestionUpliftProps> = ({
  stepKey,
  titleHtml,
  subtitleHtml,
  options,
  selectedOptionId,
  onSelectOption,
  onBack,
  onClose,
  progressPill,
}) => {
  const { eyebrow, title } = splitQuestionTitle(titleHtml);
  const questionHint = subtitleHtml
    ? subtitleHtml.replace(/<[^>]+>/g, "").trim()
    : null;
  const [pressedOptionId, setPressedOptionId] = React.useState<
    string | null
  >(null);
  const clearPressed = () => setPressedOptionId(null);

  return (
    <div
      className={styles.container}
      data-testid="assessment-question-uplift"
    >
      <AssessmentUpliftHeader onBack={onBack} onClose={onClose} />

      {progressPill && (
        <div className={styles["pill-row"]}>
          <ProgressPill {...progressPill} />
        </div>
      )}

      <div className={styles.body}>
        <AssessmentStepTransition stepKey={stepKey}>
          <div className={styles.content}>
            {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
            <h1 className={styles.title}>{title}</h1>
            {questionHint && (
              <p className={styles["question-hint"]}>{questionHint}</p>
            )}

            <div className={styles.options} data-testid="assessment-options">
              {options.map((option) => {
                const optionId = option.id ?? option.value;
                return (
                  <button
                    key={optionId}
                    type="button"
                    className={clsx(styles.option, {
                      [styles["option-selected"]]:
                        selectedOptionId != null &&
                        option.id === selectedOptionId,
                      [styles["option-pressed"]]: pressedOptionId === optionId,
                    })}
                    onClick={() => onSelectOption(option)}
                    onMouseDown={() => setPressedOptionId(optionId)}
                    onMouseUp={clearPressed}
                    onMouseLeave={clearPressed}
                    onTouchStart={() => setPressedOptionId(optionId)}
                    onTouchEnd={clearPressed}
                    onTouchCancel={clearPressed}
                  >
                    <p className={styles["option-label"]}>{option.label}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </AssessmentStepTransition>
      </div>
    </div>
  );
};

export default AssessmentQuestionUplift;
